<template>
  <v-container fluid class="dashboard-container">
    <!-- Top Bar -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between ga-3">
          <div>
            <h2 class="text-h5 text-sm-h4 font-weight-bold text-grey-darken-4 mb-1">
              Business Intelligence
            </h2>
            <p class="text-body-2 text-grey-darken-1">
              Real-time insights • Last updated: {{ formattedLastUpdate }}
            </p>
          </div>
          <div class="d-flex align-center ga-2 flex-shrink-0">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  prepend-icon="mdi-calendar-range"
                  variant="tonal"
                  color="primary"
                  size="small"
                >
                  <span class="d-none d-sm-inline">Date Range</span>
                  <span class="d-inline d-sm-none">Range</span>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="loadToday">Today</v-list-item>
                <v-list-item @click="loadThisWeek">This Week</v-list-item>
                <v-list-item @click="loadThisMonth">This Month</v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              icon="mdi-refresh"
              variant="tonal"
              :loading="loading"
              @click="refreshDashboard"
              color="primary"
              size="small"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Critical Alerts Banner -->
    <v-row v-if="criticalAlertsExist" class="mb-4">
      <v-col cols="12">
        <v-alert
          v-for="alert in topAlerts"
          :key="alert.id"
          :type="alert.type"
          :icon="getAlertIcon(alert.type)"
          class="mb-2"
          variant="tonal"
          closable
        >
          <v-alert-title class="font-weight-bold">{{ alert.title }}</v-alert-title>
          <div class="d-flex align-center justify-space-between">
            <span>{{ alert.message }}</span>
            <v-btn :to="alert.link" size="small" variant="text" append-icon="mdi-arrow-right">
              View Details
            </v-btn>
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Sales Metrics Grid -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="section-header mb-3">
          <v-icon color="primary" size="24">mdi-point-of-sale</v-icon>
          <span class="text-h6 font-weight-bold ml-2">Sales Performance</span>
        </div>
      </v-col>
      <v-col v-for="metric in dashboardMetrics" :key="metric.title" cols="12" sm="6" md="4" lg="2">
        <v-card class="metric-card" :class="`metric-${metric.color}`" elevation="0">
          <v-card-text>
            <div class="d-flex justify-space-between align-start mb-3">
              <v-avatar :color="metric.color" size="48" variant="tonal">
                <v-icon :icon="metric.icon" size="28"></v-icon>
              </v-avatar>
              <v-chip
                v-if="metric.trend"
                size="small"
                :color="metric.trend.isPositive ? 'success' : 'error'"
                variant="flat"
              >
                <v-icon
                  start
                  size="16"
                  :icon="metric.trend.direction === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down'"
                ></v-icon>
                {{ metric.trend.value.toFixed(1) }}%
              </v-chip>
            </div>
            <h3 class="text-h5 font-weight-bold mb-1">{{ metric.value }}</h3>
            <p class="text-caption text-grey-darken-1 mb-0">{{ metric.title }}</p>
            <p v-if="metric.subtitle" class="text-caption text-grey mt-1">
              {{ metric.subtitle }}
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row -->
    <v-row class="mb-4">
      <!-- Top Products Chart -->
      <v-col cols="12" md="8">
        <v-card elevation="0" class="chart-card">
          <v-card-title class="d-flex align-center justify-space-between pb-0">
            <div class="d-flex align-center">
              <v-icon color="success" class="mr-2">mdi-trending-up</v-icon>
              <span class="font-weight-bold">Top Performing Products</span>
            </div>
            <v-btn variant="text" size="small" to="/reports/products"> Full Report </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="hasTopProducts" class="chart-container">
              <canvas ref="productsChart"></canvas>
            </div>
            <v-alert v-else type="info" variant="tonal" class="mt-4">
              No product data available for the selected period
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Online vs Offline Pie Chart -->
      <v-col cols="12" md="4">
        <v-card elevation="0" class="chart-card">
          <v-card-title class="pb-0">
            <div class="d-flex align-center">
              <v-icon color="info" class="mr-2">mdi-chart-donut</v-icon>
              <span class="font-weight-bold">Sales Distribution</span>
            </div>
          </v-card-title>
          <v-card-text>
            <div v-if="hasOnlineOfflineData" class="chart-container-small">
              <canvas ref="distributionChart"></canvas>
            </div>
            <v-alert v-else type="info" variant="tonal" class="mt-4">
              No distribution data available
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Inventory and Alerts Row -->
    <v-row>
      <!-- Inventory Metrics -->
      <v-col cols="12" md="8">
        <v-card elevation="0" class="inventory-card">
          <v-card-title class="d-flex align-center justify-space-between pb-0">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-warehouse</v-icon>
              <span class="font-weight-bold">Inventory Overview</span>
            </div>
            <v-btn variant="text" size="small" to="/inventory"> Manage Inventory </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-for="metric in inventoryMetrics" :key="metric.title" cols="12" sm="6" md="3">
                <div class="inventory-metric">
                  <v-avatar :color="metric.color" size="40" variant="tonal" class="mb-2">
                    <v-icon :icon="metric.icon"></v-icon>
                  </v-avatar>
                  <h4 class="text-h6 font-weight-bold">{{ metric.value }}</h4>
                  <p class="text-caption text-grey-darken-1 mb-0">{{ metric.title }}</p>
                  <p v-if="metric.subtitle" class="text-caption text-grey">
                    {{ metric.subtitle }}
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- All Alerts Panel -->
      <v-col cols="12" md="4">
        <v-card elevation="0" class="alerts-card">
          <v-card-title class="pb-0">
            <div class="d-flex align-center">
              <v-icon color="warning" class="mr-2">mdi-bell-ring</v-icon>
              <span class="font-weight-bold">System Alerts</span>
            </div>
          </v-card-title>
          <v-card-text>
            <v-list v-if="alerts.length > 0" class="pa-0">
              <v-list-item
                v-for="alert in alerts"
                :key="alert.id"
                :to="alert.link"
                class="alert-item px-0"
              >
                <template v-slot:prepend>
                  <v-avatar :color="getAlertColor(alert.priority)" size="32" variant="tonal">
                    <v-icon :icon="getAlertIcon(alert.type)" size="18"></v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium text-body-2">
                  {{ alert.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ alert.message }}
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-icon size="20">mdi-chevron-right</v-icon>
                </template>
              </v-list-item>
            </v-list>
            <v-alert v-else type="success" variant="tonal" class="mt-2">
              <v-icon start>mdi-check-circle</v-icon>
              All systems operating normally
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useDashboardStore } from '@/stores/dashboard'
import Chart from 'chart.js/auto'
import { useAuthStore } from '@/stores/useAuthStore'

const authStore = useAuthStore()
const dashboardStore = useDashboardStore()

const {
  loading,
  dashboardMetrics,
  inventoryMetrics,
  alerts,
  lastUpdated,
  getSalesChartData,
  getOnlineVsOfflineData,
} = storeToRefs(dashboardStore)

const productsChart = ref<HTMLCanvasElement | null>(null)
const distributionChart = ref<HTMLCanvasElement | null>(null)
let productsChartInstance: Chart | null = null
let distributionChartInstance: Chart | null = null
const isCashier = authStore.user?.role?.toLowerCase() === 'cashier'

const formattedLastUpdate = computed(() => {
  if (!lastUpdated.value) return 'Never'
  return new Date(lastUpdated.value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const criticalAlertsExist = computed(() => {
  return alerts.value.some((a) => a.priority === 'critical' || a.priority === 'high')
})

const topAlerts = computed(() => {
  return alerts.value.filter((a) => a.priority === 'critical' || a.priority === 'high').slice(0, 2)
})

const hasTopProducts = computed(() => {
  return getSalesChartData.value?.labels?.length > 0
})

const hasOnlineOfflineData = computed(() => {
  return getOnlineVsOfflineData.value !== null
})

const getAlertIcon = (type: string) => {
  const icons: Record<string, string> = {
    error: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information',
    success: 'mdi-check-circle',
  }
  return icons[type] || 'mdi-bell'
}

const getAlertColor = (priority: string) => {
  const colors: Record<string, string> = {
    critical: 'error',
    high: 'warning',
    medium: 'info',
    low: 'success',
  }
  return colors[priority] || 'grey'
}

const refreshDashboard = async () => {
  await dashboardStore.refreshDashboard()
  await nextTick()
  renderCharts()
}

const loadToday = async () => {
  const today = new Date().toISOString()
  await dashboardStore.loadDashboardData(today, today)
  await nextTick()
  renderCharts()
}

const loadThisWeek = async () => {
  const today = new Date()
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - today.getDay())
  await dashboardStore.loadDashboardData(weekStart.toISOString(), today.toISOString())
  await nextTick()
  renderCharts()
}

const loadThisMonth = async () => {
  const today = new Date()
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  await dashboardStore.loadDashboardData(monthStart.toISOString(), today.toISOString())
  await nextTick()
  renderCharts()
}

const renderCharts = () => {
  if (productsChart.value && hasTopProducts.value) {
    if (productsChartInstance) {
      productsChartInstance.destroy()
    }
    const ctx = productsChart.value.getContext('2d')
    if (ctx) {
      productsChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: getSalesChartData.value.labels,
          datasets: [
            {
              label: 'Revenue',
              data: getSalesChartData.value.datasets[0].data,
              backgroundColor: 'rgba(76, 175, 80, 0.8)',
              borderRadius: 8,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
            },
            x: {
              grid: { display: false },
            },
          },
        },
      })
    }
  }

  if (distributionChart.value && hasOnlineOfflineData.value) {
    if (distributionChartInstance) {
      distributionChartInstance.destroy()
    }
    const ctx = distributionChart.value.getContext('2d')
    if (ctx) {
      distributionChartInstance = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: getOnlineVsOfflineData.value!.labels,
          datasets: [
            {
              data: getOnlineVsOfflineData.value!.datasets[0].data,
              backgroundColor: getOnlineVsOfflineData.value!.datasets[0].backgroundColor,
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { padding: 15, font: { size: 12 } },
            },
          },
        },
      })
    }
  }
}

watch([getSalesChartData, getOnlineVsOfflineData], async () => {
  await nextTick()
  renderCharts()
})

onMounted(async () => {
  await dashboardStore.refreshDashboard()
  await nextTick()
  renderCharts()
})
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 24px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  padding-left: 4px;
}

.metric-card {
  height: 100%;
  border-radius: 16px !important;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  background: white;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.metric-success {
  border-color: rgba(76, 175, 80, 0.2);
}

.metric-error {
  border-color: rgba(244, 67, 54, 0.2);
}

.metric-warning {
  border-color: rgba(255, 152, 0, 0.2);
}

.metric-info {
  border-color: rgba(33, 150, 243, 0.2);
}

.metric-primary {
  border-color: rgba(103, 58, 183, 0.2);
}

.metric-secondary {
  border-color: rgba(158, 158, 158, 0.2);
}

.chart-card,
.inventory-card,
.alerts-card {
  border-radius: 16px !important;
  background: white;
  height: 100%;
}

.chart-container {
  height: 300px;
  margin-top: 16px;
}

.chart-container-small {
  height: 280px;
  margin-top: 16px;
}

.inventory-metric {
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.inventory-metric:hover {
  background: rgba(0, 0, 0, 0.04);
  transform: scale(1.02);
}

.alert-item {
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.alert-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

.v-alert {
  border-radius: 12px !important;
}
</style>
